/**
 * Shared organization-role vocabulary and predicates (Issue #558).
 *
 * Canonical values for user.org_role:
 *   owner      reserved for bootstrap/provisioned org owners (tenant bootstrap,
 *              demo seeding); grants the same privileges as org_admin everywhere
 *   org_admin  promoted organization administrator
 *   member     regular member
 *
 * The bare legacy 'admin' literal is accepted only as a read-time compatibility
 * alias while the runtime migration backfills existing tenant records. No
 * organization-role writer emits it.
 */
export const ORG_ADMIN_ROLES = ['owner', 'org_admin'];
export const LEGACY_ORG_ADMIN_ROLES = ['admin'];
export const ORG_ADMIN_ROLE_VALUES = [...ORG_ADMIN_ROLES, ...LEGACY_ORG_ADMIN_ROLES];

export function isOrgAdminUser(user) {
    return !!user && (ORG_ADMIN_ROLE_VALUES.includes(user.org_role) || !!user.is_admin);
}

// ─── Effective-access preview (UI mirror of the backend save rules) ─────────
// backend rbac.py::sync_legacy_fields is the authority that decides what a
// save stores in the legacy `role`/`is_admin` pair: org admins (and owners)
// always keep role 'admin' + is_admin true; members store Sales Rep unless
// their highest department role is Manager (or Admin/Developer, which also
// makes them a full org admin via is_admin). The edit dialog mirrors these
// rules so the admin sees the true outcome BEFORE saving instead of a
// "false positive" save that reverts on reopen (QA report, #558 follow-on).
const PILLAR_ROLE_RANK = { user: 0, manager: 1, admin: 2, developer: 3 };

// Highest role across a pillar_roles map ({ [pillarId]: { role, read_only } }),
// mirroring pillar_map.highest_role() — unknown roles rank -1, first max wins.
export function highestPillarRole(pillarRoles) {
    let best = null;
    let bestPillarId = null;
    let bestRank = -1;
    for (const [pillarId, entry] of Object.entries(pillarRoles || {})) {
        const role = entry?.role;
        const rank = PILLAR_ROLE_RANK[role] ?? -1;
        if (rank > bestRank) {
            bestRank = rank;
            best = role;
            bestPillarId = pillarId;
        }
    }
    return best === null ? null : { role: best, pillarId: bestPillarId };
}

/**
 * Predict exactly what the backend will end up storing for (`role`, `is_admin`)
 * on a save — the same decision `sync_legacy_fields(pillar_roles, org_role)`
 * makes (runtime/backend/rbac.py).
 *
 * Returns { isOrgAdmin, effectiveWorkRole, origin, driverPillarId }:
 *   isOrgAdmin        — does this person pass the is_org_admin() gate?
 *   effectiveWorkRole — 'admin' | 'manager' | 'sales_rep' (the stored `role`)
 *   origin — 'org_role' (org admin/owner: Admin work role is forced)
 *            'pillar'  (member: highest department role drives the outcome)
 *            'default' (member: no department access → Sales Rep)
 *   driverPillarId — the department deciding the outcome (origin 'pillar')
 */
export function computeEffectiveAccess(orgRole, pillarRoles) {
    if (ORG_ADMIN_ROLE_VALUES.includes(orgRole)) {
        return { isOrgAdmin: true, effectiveWorkRole: 'admin', origin: 'org_role', driverPillarId: null };
    }
    const best = highestPillarRole(pillarRoles);
    if (!best) {
        return { isOrgAdmin: false, effectiveWorkRole: 'sales_rep', origin: 'default', driverPillarId: null };
    }
    if (best.role === 'admin' || best.role === 'developer') {
        return { isOrgAdmin: true, effectiveWorkRole: 'admin', origin: 'pillar', driverPillarId: best.pillarId };
    }
    if (best.role === 'manager') {
        return { isOrgAdmin: false, effectiveWorkRole: 'manager', origin: 'pillar', driverPillarId: best.pillarId };
    }
    // Member whose highest department role is `user` (or unrecognized).
    return { isOrgAdmin: false, effectiveWorkRole: 'sales_rep', origin: 'pillar', driverPillarId: best.pillarId };
}
